import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  useListDiligentRiskTemplates,
  useListTemplateQuestions,
  useGetRiskMethodology,
  useUpdateDiligentTemplateQuestions,
} from "../../custom-hooks/queries";
import { computeCriticality, resolveConfig, aggregateSection } from "../RFIS/readonly/riskAggregates";
import NotificationModal from "../../components/NotificationModal";
import {
  naturalCompare,
  Pill,
  AnchoredValueStepper,
  ColHeader,
  TemplateOptions,
  SectionToggleHeader,
  QuestionCell,
} from "../../components/RiskModels/riskModelEditorShared";
import {
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  ArrowUturnLeftIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";

/**
 * Diligent admin edits the Diligent Risk Model BASE values
 * (Impact / Likelihood / Detectability) for a template.
 *
 * Route: /Backend/RiskModels/Diligent
 *
 * Template-scoped (not sponsor-scoped): one edit affects future scoring of every
 * sponsor's VQQs on the template. The sibling page (/Backend/RiskModels) authors
 * sponsor+template CUSTOM models layered on top of these base values.
 *
 * Per question, I/L/D are 1–5 dropdowns with the verbatim anchored label on
 * hover (Detectability's inverted scale shown explicitly). Question Risk Score /
 * RAG / Criticality and the Section Risk Score (Mean+0.5·StDev) / Section RAG /
 * Section Criticality (bucketed-mean) all re-derive live. Saves persist to the
 * platform source of truth (Airtable + local mirror); published VQQ scores are
 * never re-scored.
 */

// Question RAG from the (dynamic) config bands — mirrors Airtable's Question RAG.
const ragForScore = (score, config) => {
  if (score == null) return null;
  for (const b of config?.question_rag_bands ?? []) {
    const min = b.min ?? 0;
    const max = b.max ?? Infinity;
    if (score >= min && score <= max) return b.band;
  }
  return null;
};

const parseAnchors = (json) => {
  if (!json || typeof json !== "string") return {};
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return {};
    const out = {};
    for (const item of arr) {
      const lvl = Number(item?.level);
      if (Number.isFinite(lvl)) out[lvl] = { level: lvl, anchor: item?.anchor ?? "", definition: item?.definition ?? "" };
    }
    return out;
  } catch {
    return {};
  }
};

const parseRefs = (json) => {
  if (!json || typeof json !== "string") return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return [];
    return arr
      .map((r) => ({ title: (r?.title ?? "").trim(), data: (r?.data ?? "").trim() }))
      .filter((r) => r.title || r.data);
  } catch {
    return [];
  }
};

const RAG_STYLES = {
  Red: "bg-red-100 text-red-800 border-red-200",
  Yellow: "bg-amber-100 text-amber-800 border-amber-200",
  Green: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const CRIT_STYLES = {
  Critical: "bg-rose-100 text-rose-800 border-rose-200",
  Major: "bg-orange-100 text-orange-800 border-orange-200",
  Minor: "bg-slate-100 text-slate-700 border-slate-200",
};

/** 1–5 stepper bound to the base value; amber when changed from last-saved.
 *  The shared stepper renders the anchors and hover copy. */
const AnchoredStepper = ({ value, changed, anchors, inverted, dimName, onChange }) => (
  <AnchoredValueStepper
    value={value ?? null}
    dimName={dimName}
    anchors={anchors}
    inverted={inverted}
    highlighted={changed}
    tone="amber"
    onStep={onChange}
  />
);

const RowStatus = ({ status }) => {
  if (!status) return null;
  if (status.state === "saving") {
    return (
      <svg className="h-4 w-4 animate-spin text-indigo-500" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      </svg>
    );
  }
  if (status.state === "saved") return <CheckCircleIcon className="h-4 w-4 text-emerald-500" title="Saved" />;
  if (status.state === "error") return <ExclamationCircleIcon className="h-4 w-4 text-rose-500" title={status.msg || "Couldn't save"} />;
  return null;
};

const GRID = "grid grid-cols-[minmax(16rem,1fr)_repeat(3,7rem)_3.5rem_4.5rem_5rem_4rem] gap-2";

const AdminDiligentRiskModel = () => {
  const [templateId, setTemplateId] = useState("");
  const [working, setWorking] = useState({}); // id -> { i, l, d }
  const [baseline, setBaseline] = useState({}); // last-saved snapshot to diff against
  const [rowStatus, setRowStatus] = useState({}); // id -> { state: "saving"|"saved"|"error", msg? }
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [notification, setNotification] = useState({ open: false, title: "", desc: "", error: false });

  const { data: templates } = useListDiligentRiskTemplates();
  const { data: questions, isLoading, isError } = useListTemplateQuestions(templateId, { enabled: !!templateId });
  const { data: methodology } = useGetRiskMethodology({});
  const config = useMemo(() => resolveConfig(methodology), [methodology]);
  const saveMutation = useUpdateDiligentTemplateQuestions();

  const anchorMaps = useMemo(
    () => ({
      Impact: parseAnchors(methodology?.impact_anchors_json),
      Likelihood: parseAnchors(methodology?.likelihood_anchors_json),
      Detectability: parseAnchors(methodology?.detectability_anchors_json),
    }),
    [methodology]
  );

  // Seed working + baseline whenever the loaded question set changes.
  useEffect(() => {
    if (!Array.isArray(questions)) {
      setWorking({});
      setBaseline({});
      return;
    }
    const seed = {};
    for (const q of questions) {
      seed[q.diligent_risk_question_id] = { i: q.impact ?? null, l: q.likelihood ?? null, d: q.detectability ?? null };
    }
    setWorking(seed);
    setBaseline(seed);
    setRowStatus({});
  }, [questions]);

  const setDim = useCallback((id, dim, val) => {
    setWorking((prev) => ({ ...prev, [id]: { ...prev[id], [dim]: val } }));
    // Editing clears any prior saved/error badge on the row.
    setRowStatus((prev) => {
      if (!prev[id]) return prev;
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
  }, []);

  const isDirty = useCallback(
    (id) => {
      const w = working[id];
      const b = baseline[id];
      if (!w || !b) return false;
      return w.i !== b.i || w.l !== b.l || w.d !== b.d;
    },
    [working, baseline]
  );

  const dirtyIds = useMemo(() => Object.keys(working).filter(isDirty), [working, isDirty]);

  const derive = useCallback(
    (id) => {
      const { i = null, l = null, d = null } = working[id] || {};
      const rs = i != null && l != null && d != null ? i * l * d : null;
      return { i, l, d, rs, rag: ragForScore(rs, config), crit: rs != null ? computeCriticality(rs, i, config) : null };
    },
    [working, config]
  );

  const sortedFiltered = useMemo(() => {
    const arr = [...(questions ?? [])].sort((a, b) => naturalCompare(a.question_code, b.question_code));
    const s = search.trim().toLowerCase();
    if (!s) return arr;
    return arr.filter((q) =>
      [q.question_code, q.question_text_snapshot, q.section_name].filter(Boolean).join(" ").toLowerCase().includes(s)
    );
  }, [questions, search]);

  const groupedSections = useMemo(() => {
    const map = new Map();
    for (const q of sortedFiltered) {
      const key = q.section_name || "Uncategorized";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(q);
    }
    return map;
  }, [sortedFiltered]);

  // Live section derivation — Section Risk Score (Mean+0.5·StDev), Section RAG,
  // Section Criticality (bucketed-mean). Same classifier the scoring engine uses.
  const sectionAgg = useCallback(
    (qs) => {
      const rows = qs.map((q) => {
        const { i = null, l = null, d = null } = working[q.diligent_risk_question_id] || {};
        return { rubric_impact: i, rubric_likelihood: l, rubric_detectability: d };
      });
      return aggregateSection(rows, methodology);
    },
    [working, methodology]
  );

  const toggleSection = useCallback((name) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setWorking(baseline);
    setRowStatus({});
  }, [baseline]);

  // Save the given question ids (one for per-question save, all dirty for batch).
  const saveQuestions = useCallback(
    async (ids) => {
      if (!ids || ids.length === 0) return;
      const payload = ids.map((id) => ({
        diligent_risk_question_id: id,
        impact: working[id].i,
        likelihood: working[id].l,
        detectability: working[id].d,
      }));
      setRowStatus((prev) => {
        const next = { ...prev };
        for (const id of ids) next[id] = { state: "saving" };
        return next;
      });
      try {
        const res = await saveMutation.mutateAsync({ diligentRiskTemplateId: templateId, questions: payload });
        const data = res?.data || {};
        const updated = new Map((data.updated || []).map((u) => [u.diligent_risk_question_id, u]));
        const skipped = new Map((data.skipped || []).map((s) => [s.diligent_risk_question_id, s.reason]));

        setBaseline((prev) => {
          const next = { ...prev };
          for (const [id, u] of updated) next[id] = { i: u.impact, l: u.likelihood, d: u.detectability };
          return next;
        });
        setRowStatus((prev) => {
          const next = { ...prev };
          for (const id of ids) {
            if (updated.has(id)) next[id] = { state: "saved" };
            else if (skipped.has(id)) next[id] = { state: "error", msg: skipped.get(id) };
            else next[id] = { state: "error", msg: data.warning || "Not saved" };
          }
          return next;
        });

        if (data.warning || (data.skipped?.length ?? 0) > 0) {
          setNotification({
            open: true,
            error: (data.skipped?.length ?? 0) > 0,
            title: (data.updated_count ?? 0) > 0 ? `Saved ${data.updated_count}` : "Some changes not saved",
            desc: [data.warning, (data.skipped?.length ?? 0) > 0 ? "See the row indicators for what didn't save." : null]
              .filter(Boolean)
              .join(" "),
          });
        }

        const savedIds = ids.filter((id) => updated.has(id));
        if (savedIds.length) {
          setTimeout(() => {
            setRowStatus((prev) => {
              const next = { ...prev };
              for (const id of savedIds) if (next[id]?.state === "saved") delete next[id];
              return next;
            });
          }, 2500);
        }
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || "Couldn't save.";
        setRowStatus((prev) => {
          const next = { ...prev };
          for (const id of ids) next[id] = { state: "error", msg };
          return next;
        });
        setNotification({ open: true, error: true, title: "Save failed", desc: msg });
      }
    },
    [working, templateId, saveMutation]
  );

  const selectedTemplate = useMemo(
    () => (templates || []).find((t) => t.diligent_risk_template_id === templateId) || null,
    [templates, templateId]
  );

  const saving = saveMutation.isPending;

  return (
    <div className="px-6 py-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-indigo-100 text-indigo-700 shrink-0">
            <ShieldCheckIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Diligent Risk Model — Template Editor</h1>
            <p className="text-[13px] text-gray-500 mt-0.5 max-w-2xl">
              Edit the base Impact / Likelihood / Detectability for a template's questions. Changes affect future
              scoring of every sponsor's VQQs on this template. Published scores are not re-scored.
            </p>
          </div>
        </div>
        <Link
          to="/Backend/RiskModels"
          className="shrink-0 text-[13px] font-medium text-indigo-600 hover:text-indigo-700 hover:underline whitespace-nowrap mt-1"
        >
          Custom Models →
        </Link>
      </div>

      {/* Template picker */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 mb-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Scope</div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[18rem] max-w-2xl">
            <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
            <select
              value={templateId}
              onChange={(e) => {
                setTemplateId(e.target.value);
                setSearch("");
                setCollapsed(new Set());
              }}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">— pick a template —</option>
              <TemplateOptions templates={templates} />
            </select>
          </div>
        </div>
      </div>

      {!templateId ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center text-sm text-gray-500">
          Pick a template above to edit its Diligent Risk Model values.
        </div>
      ) : isLoading ? (
        <div className="py-12 text-center text-sm text-gray-500">Loading questions…</div>
      ) : isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Couldn't load this template's questions. Please try again.
        </div>
      ) : (
        <>
          {/* Action bar — batch save */}
          <div className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-white/95 backdrop-blur border-b border-gray-200 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{selectedTemplate?.template_name}</p>
              <p className="text-[12px] text-gray-500">
                {questions?.length ?? 0} questions
                {dirtyIds.length > 0 ? (
                  <span className="text-amber-600 font-medium"> · {dirtyIds.length} unsaved</span>
                ) : (
                  <span className="text-gray-400"> · no pending changes</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleReset}
                disabled={dirtyIds.length === 0 || saving}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                <ArrowUturnLeftIcon className="h-4 w-4" />
                Reset
              </button>
              <button
                type="button"
                onClick={() => saveQuestions(dirtyIds)}
                disabled={dirtyIds.length === 0 || saving}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-semibold border shadow-sm ${
                  dirtyIds.length === 0 || saving
                    ? "bg-indigo-300 text-white border-indigo-300 cursor-not-allowed"
                    : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {saving ? "Saving…" : `Save all${dirtyIds.length > 0 ? ` (${dirtyIds.length})` : ""}`}
              </button>
            </div>
          </div>

          {/* Search + hint */}
          <div className="flex items-center justify-between gap-4 mt-4 mb-2">
            <div className="relative w-72">
              <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code, text, section…"
                className="w-full rounded-md border border-gray-300 bg-white pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
              <InformationCircleIcon className="h-3.5 w-3.5" />
              Hover a value for its anchored definition. Detectability is inverted (5 = hardest to catch).
            </div>
          </div>

          {/* Table: one column never narrower than a row's minimum, so a phone
              scrolls it sideways instead of squeezing the question text. */}
          <div className="rounded-lg border border-gray-200 overflow-x-auto grid grid-cols-[minmax(min-content,1fr)]">
            <div className={`${GRID} px-4 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-500`}>
              <div>Question</div>
              <div className="text-center"><ColHeader label="Impact" anchors={anchorMaps.Impact} dimName="Impact" /></div>
              <div className="text-center"><ColHeader label="Likelihood" anchors={anchorMaps.Likelihood} dimName="Likelihood" /></div>
              <div className="text-center"><ColHeader label="Detect. (inv)" anchors={anchorMaps.Detectability} dimName="Detectability" inverted /></div>
              <div className="text-center">Risk</div>
              <div className="text-center">RAG</div>
              <div className="text-center">Criticality</div>
              <div className="text-center">Save</div>
            </div>

            {[...groupedSections.entries()].map(([sectionName, qs]) => {
              const agg = sectionAgg(qs);
              const isCollapsed = collapsed.has(sectionName);
              return (
                <React.Fragment key={sectionName}>
                  <SectionToggleHeader
                    sectionName={sectionName}
                    count={qs.length}
                    isCollapsed={isCollapsed}
                    onToggle={() => toggleSection(sectionName)}
                  >
                    <span className="ml-auto flex items-center gap-4">
                      <span className="flex items-center gap-1.5" title="Mean(Question Risk Score) + 0.5 × sample StDev">
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">Section Risk Score</span>
                        <span
                          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[12px] font-semibold tabular-nums ${
                            RAG_STYLES[agg?.intrinsic_section_risk_rag] || "bg-gray-100 text-gray-600 border-gray-200"
                          }`}
                        >
                          {agg?.intrinsic_section_risk_score ?? "—"}
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5" title="Bucketed mean of question criticality weights (5/3/1)">
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">Section Criticality</span>
                        <Pill label={agg?.criticality} styles={CRIT_STYLES} />
                      </span>
                    </span>
                  </SectionToggleHeader>

                  {!isCollapsed &&
                    qs.map((q) => {
                      const id = q.diligent_risk_question_id;
                      const dirty = isDirty(id);
                      const b = baseline[id] || {};
                      const { i, l, d, rs, rag, crit } = derive(id);
                      const refs = parseRefs(q.regulatory_refs_json);
                      const status = rowStatus[id];
                      return (
                        <div
                          key={id}
                          className={`${GRID} px-4 py-2.5 items-center border-b border-gray-100 last:border-b-0 ${
                            dirty ? "bg-amber-50/40" : "hover:bg-gray-50/60"
                          }`}
                        >
                          <QuestionCell
                            code={q.question_code}
                            badge={dirty ? "edited" : null}
                            badgeTone="amber"
                            text={q.question_text_snapshot}
                          >
                            {refs.length > 0 ? (
                              <div className="mt-1 flex flex-wrap items-center gap-1" title="Regulatory references (read-only, managed separately)">
                                <span className="text-[9px] uppercase tracking-wide text-gray-300">Refs</span>
                                {refs.slice(0, 6).map((r, idx) => (
                                  <span
                                    key={`${r.title}-${idx}`}
                                    className="inline-flex items-center rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-500"
                                    title={[r.title, r.data].filter(Boolean).join(": ")}
                                  >
                                    {r.title || r.data}
                                  </span>
                                ))}
                                {refs.length > 6 ? <span className="text-[10px] text-gray-400">+{refs.length - 6}</span> : null}
                              </div>
                            ) : null}
                          </QuestionCell>

                          <div className="flex justify-center">
                            <AnchoredStepper value={i} changed={dirty && i !== b.i} anchors={anchorMaps.Impact} dimName="Impact" onChange={(v) => setDim(id, "i", v)} />
                          </div>
                          <div className="flex justify-center">
                            <AnchoredStepper value={l} changed={dirty && l !== b.l} anchors={anchorMaps.Likelihood} dimName="Likelihood" onChange={(v) => setDim(id, "l", v)} />
                          </div>
                          <div className="flex justify-center">
                            <AnchoredStepper value={d} changed={dirty && d !== b.d} anchors={anchorMaps.Detectability} dimName="Detectability" inverted onChange={(v) => setDim(id, "d", v)} />
                          </div>

                          <div className="text-center text-sm font-semibold tabular-nums text-gray-900">{rs ?? "—"}</div>
                          <div className="flex justify-center">
                            <Pill label={rag} styles={RAG_STYLES} />
                          </div>
                          <div className="flex justify-center">
                            <Pill label={crit} styles={CRIT_STYLES} />
                          </div>

                          <div className="flex items-center justify-center gap-1.5">
                            {status ? (
                              <RowStatus status={status} />
                            ) : dirty ? (
                              <button
                                type="button"
                                onClick={() => saveQuestions([id])}
                                title="Save this question"
                                className="rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-100"
                              >
                                Save
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                </React.Fragment>
              );
            })}

            {groupedSections.size === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                {search ? "No questions match your search." : "This template has no questions."}
              </div>
            ) : null}
          </div>
        </>
      )}

      <NotificationModal
        open={notification.open}
        setOpen={(open) => setNotification((prev) => ({ ...prev, open }))}
        title={notification.title}
        desc={notification.desc}
        error={notification.error}
      />
    </div>
  );
};

export default AdminDiligentRiskModel;
